import React from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Trash2,
  UserCheck,
  UserX,
  Ban,
  Shield,
  CreditCard,
  Calendar,
  MessageSquare,
  X
} from 'lucide-react';
import '../styles/action-confirm-modal.css';

/**
 * Reusable Action Confirmation Modal
 * Use for all vital/destructive actions across the app
 */
const ActionConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'default', // 'default', 'danger', 'warning', 'success'
  icon, // custom icon component
  loading = false,
  loadingText = 'Processing...',
  infoText, // optional info/warning text
  details // optional key-value details to show
}) => {
  if (!isOpen) return null;

  // Default icons based on type
  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'danger':
        return <XCircle size={28} />;
      case 'warning':
        return <AlertTriangle size={28} />;
      case 'success':
        return <CheckCircle size={28} />;
      default:
        return <AlertCircle size={28} />;
    }
  };

  const getHeaderClass = () => {
    switch (type) {
      case 'danger':
        return 'header-danger';
      case 'warning':
        return 'header-warning';
      case 'success':
        return 'header-success';
      default:
        return 'header-default';
    }
  };

  const getConfirmClass = () => {
    switch (type) {
      case 'danger':
        return 'btn-danger';
      case 'warning':
        return 'btn-warning';
      case 'success':
        return 'btn-success';
      default:
        return 'btn-primary';
    }
  };

  return (
    <div className="action-confirm-overlay" onClick={onClose}>
      <div className="action-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className={`action-confirm-header ${getHeaderClass()}`}>
          {getIcon()}
          <h3>{title}</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="action-confirm-body">
          {message && <p className="confirm-message">{message}</p>}
          {description && <p className="confirm-description">{description}</p>}
          
          {details && (
            <div className="confirm-details">
              {Object.entries(details).map(([key, value]) => (
                <div key={key} className="detail-row">
                  <span className="detail-label">{key}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
            </div>
          )}
          
          {infoText && (
            <div className={`confirm-info ${type === 'danger' ? 'info-danger' : 'info-warning'}`}>
              <AlertCircle size={18} />
              <span>{infoText}</span>
            </div>
          )}
        </div>
        
        <div className="action-confirm-footer">
          <button className="btn-cancel" onClick={onClose} disabled={loading}>
            {cancelText}
          </button>
          <button 
            className={`btn-confirm ${getConfirmClass()}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" />
                {loadingText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Pre-configured modal types for common actions
export const ConfirmTypes = {
  ACCEPT_JOB: {
    title: 'Accept This Job?',
    type: 'success',
    icon: <CheckCircle size={28} />,
    confirmText: 'Yes, Accept Job',
    infoText: 'Once accepted, you commit to completing this job. The client will be notified.'
  },
  DECLINE_JOB: {
    title: 'Decline This Job?',
    type: 'danger',
    icon: <XCircle size={28} />,
    confirmText: 'Yes, Decline',
    infoText: 'This action cannot be undone. The client will be notified of your decision.'
  },
  SELECT_HERO: {
    title: 'Select This Hero?',
    type: 'success',
    icon: <UserCheck size={28} />,
    confirmText: 'Yes, Select Hero',
    infoText: 'Your payment authorization will be held until job completion.'
  },
  CANCEL_JOB: {
    title: 'Cancel This Job?',
    type: 'danger',
    icon: <Ban size={28} />,
    confirmText: 'Yes, Cancel Job',
    infoText: 'This will cancel the job and notify all parties involved.'
  },
  COMPLETE_JOB: {
    title: 'Mark Job Complete?',
    type: 'success',
    icon: <CheckCircle size={28} />,
    confirmText: 'Yes, Complete Job',
    infoText: 'Payment will be released to the hero after confirmation.'
  },
  DELETE_ITEM: {
    title: 'Delete This Item?',
    type: 'danger',
    icon: <Trash2 size={28} />,
    confirmText: 'Yes, Delete',
    infoText: 'This action cannot be undone.'
  },
  SUSPEND_USER: {
    title: 'Suspend This User?',
    type: 'danger',
    icon: <UserX size={28} />,
    confirmText: 'Yes, Suspend User',
    infoText: 'The user will lose access to their account until unsuspended.'
  },
  VERIFY_USER: {
    title: 'Verify This User?',
    type: 'success',
    icon: <Shield size={28} />,
    confirmText: 'Yes, Verify User',
    infoText: 'This will grant the user verified status.'
  },
  PROCESS_PAYMENT: {
    title: 'Process Payment?',
    type: 'warning',
    icon: <CreditCard size={28} />,
    confirmText: 'Yes, Process Payment',
    infoText: 'This will charge the payment method on file.'
  },
  BOOK_APPOINTMENT: {
    title: 'Confirm Booking?',
    type: 'success',
    icon: <Calendar size={28} />,
    confirmText: 'Yes, Book Now',
    infoText: 'The hero will be notified of your booking request.'
  }
};

export default ActionConfirmModal;
